import { useNavigate, useParams } from "react-router-dom";
import { toastr } from "react-redux-toastr";
import { useForm } from "../../../hooks/useForm";
import {
  useGetCallcenterQuery,
  useSaveCallcenterMutation,
} from "../../../services/docs";
import { Spinner } from "../../../components/Spinner";

export const CallcenterScreen = () => {
  const { siniestro, tramite_id = "0" } = useParams();
  const navigate = useNavigate();

  // Queries & Mutations
  const { data: callcenter, isLoading: isLoadingQuery } = useGetCallcenterQuery({
    siniestro,
    tramite: tramite_id,
  });
  const [saveCallcenter, { isLoading: cargandoBoton }] = useSaveCallcenterMutation();

  // Custom Form Hook
  const {
    formulario,
    handleInputChange,
    handleTexareChange,
    handleSelectChange,
  } = useForm({
    fc_llamada_id: "",
    fi_calificacion_llamada: "",
    fc_comentarios_llamada: "",
    tramite_id: Number(tramite_id),
    siniestro: siniestro || "",
  });

  const handleAction = async () => {
    // 1. Validación Frontend Básica
    if (!formulario.fc_llamada_id.trim()) {
      toastr.warning("Debe ingresar el ID de la llamada");
      return;
    }
    if (!formulario.fi_calificacion_llamada || formulario.fi_calificacion_llamada === "0") {
      toastr.warning("Debe seleccionar una calificación para la llamada");
      return;
    }

    // 2. Envío a la API
    try {
      const resp = await saveCallcenter({
        ...formulario,
        tramite_id: +(tramite_id || 0),
        fi_calificacion_llamada: +formulario.fi_calificacion_llamada,
      }).unwrap();

      if (resp) {
        toastr.success(resp.message || "Llamada registrada correctamente");
        navigate(`/view/siniestro/${siniestro}/${tramite_id}/proceso`); // Typo corregido (processo -> proceso)
      }
    } catch (err: any) {
      if (err?.status === 400 && err?.data?.message) {
        if (Array.isArray(err.data.message)) {
          err.data.message.forEach((m: string) => toastr.error(m));
        } else {
          toastr.error(err.data.message);
        }
      } else {
        toastr.error(err?.data?.message || "Ocurrió un error al registrar la llamada");
      }
    }
  };

  if (isLoadingQuery) {
    return (
      <div className="flex h-64 w-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-6 pt-4">
      {/* Contenedor principal centrado */}
      <div className="mx-auto max-w-3xl">
        <section className="rounded-xl border border-slate-200 bg-white shadow-sm">

          {/* --- Cabecera de la Tarjeta --- */}
          <div className="border-b border-slate-100 bg-slate-50/50 px-6 py-4">
            <h2 className="flex items-center gap-2 text-sm font-bold text-slate-800">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path></svg>
              </div>
              Registro de Contacto / Call Center
            </h2>
            <p className="ml-9 mt-1 text-[11px] text-slate-500">
              Documente el historial de llamadas y la resolución brindada al asegurado.
            </p>
          </div>

          {/* --- Cuerpo del Formulario --- */}
          <div className="flex flex-col gap-5 p-6">

            {/* Campo: ID de Llamada y Calificación (Grid en pantallas medianas) */}
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <div>
                <label htmlFor="fc_llamada_id" className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-slate-600">
                  ID de Llamada / Grabación
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-slate-400">
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"></path></svg>
                  </span>
                  <input
                    type="text"
                    id="fc_llamada_id"
                    name="fc_llamada_id"
                    placeholder="Ej. CALL-001928"
                    onChange={handleInputChange}
                    value={formulario.fc_llamada_id}
                    className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50/50 pl-9 pr-3 text-sm font-medium text-slate-900 transition-all focus:border-brand-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="fi_calificacion_llamada" className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-slate-600">
                  Calificación de Interacción
                </label>
                <select
                  id="fi_calificacion_llamada"
                  name="fi_calificacion_llamada"
                  onChange={handleSelectChange}
                  value={formulario.fi_calificacion_llamada}
                  className="h-11 w-full cursor-pointer rounded-xl border border-slate-200 bg-slate-50/50 px-3 text-sm font-medium text-slate-900 transition-all hover:bg-slate-50 focus:border-brand-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                >
                  <option value={0} disabled>Seleccione una opción...</option>
                  {callcenter?.calificaciones?.map((r: any) => (
                    <option key={r.id} value={Number(r.id)}>
                      {r.fc_calificacion}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Campo: Comentarios */}
            <div>
              <label htmlFor="fc_comentarios_llamada" className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-slate-600">
                Comentarios / Acuerdos
              </label>
              <textarea
                id="fc_comentarios_llamada"
                name="fc_comentarios_llamada"
                onChange={handleTexareChange}
                value={formulario.fc_comentarios_llamada}
                placeholder="Escriba un resumen de la llamada y las acciones a tomar..."
                rows={4}
                className="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3 py-2 text-sm font-medium text-slate-900 transition-all focus:border-brand-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-500/20"
              ></textarea>
            </div>

          </div>

          {/* --- Footer de Acción --- */}
          <div className="flex items-center justify-end border-t border-slate-100 bg-slate-50/50 p-4">
            <button
              onClick={handleAction}
              disabled={cargandoBoton}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-600 px-6 py-2.5 text-sm font-bold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:bg-brand-700 disabled:opacity-50"
            >
              {cargandoBoton ? <Spinner /> : (
                <>
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"></path></svg>
                  Guardar Llamada
                </>
              )}
            </button>
          </div>

        </section>
      </div>
    </div>
  );
};
